import { HttpInterceptorFn } from '@angular/common/http';
import { environment } from '../../environments/environment';

/**
 * El access token ya no se inyecta como header `Authorization` desde localStorage:
 * vive en una cookie HttpOnly que el backend setea en el login. Para que el browser
 * mande esa cookie en cada request a la API, clonamos la request con
 * `withCredentials: true` (necesario también para que CORS acepte credenciales).
 *
 * Sólo se manda `withCredentials` a la API real, NO a cualquier URL que contenga
 * `/api/`. Decidir por substring (`req.url.includes('/api/')`) filtraría también a
 * `https://atacante.com/api/...`, mandándole la cookie. Por eso matcheamos contra:
 *   - el origin exacto de `environment.apiUrl` cuando es absoluta (deploy cross-origin), o
 *   - rutas relativas que arrancan con `/api/` (dev: el proxy de Angular sirve la API
 *     same-origin, `apiUrl='/api/v1'`).
 */

// Origin de la API si `apiUrl` es absoluta (ej: 'https://api.example.com/api/v1' -> 'https://api.example.com').
// Si es relativa ('/api/v1'), `URL(...)` con base del documento da el origin propio; lo
// detectamos por separado vía el prefijo de path para no atar la decisión al host de runtime.
const apiOrigin: string | null = (() => {
  try {
    return new URL(environment.apiUrl).origin;
  } catch {
    return null; // apiUrl es relativa: no hay origin absoluto que comparar.
  }
})();

function isApiRequest(url: string): boolean {
  if (apiOrigin) {
    try {
      // URL absoluta de la API: el request tiene que ir EXACTAMENTE a ese origin.
      return new URL(url).origin === apiOrigin;
    } catch {
      return false;
    }
  }
  // apiUrl relativa (dev/same-origin): aceptamos rutas que arrancan con `/api/`.
  return url.startsWith('/api/');
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req.url)) return next(req);

  return next(req.clone({ withCredentials: true }));
};
